
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Game } from '@/types/game';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useDeviceOrientation } from '@/hooks/useDeviceOrientation';

interface LandscapeGamePlayerProps {
  game: Game;
  onExit: () => void;
}

const LandscapeGamePlayer: React.FC<LandscapeGamePlayerProps> = ({ game, onExit }) => {
  const [gameLoaded, setGameLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { orientation } = useDeviceOrientation();

  const handleGameLoad = useCallback(() => {
    console.log('Responsive game loaded successfully');
    setGameLoaded(true);
  }, []);

  const handleExit = useCallback(() => {
    // Reset viewport and body styles
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1');
    }
    
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.style.margin = '';
    document.body.style.padding = '';
    document.documentElement.style.overflow = '';
    document.documentElement.style.height = '';
    
    onExit();
  }, [onExit]);

  const updateGameDimensions = useCallback(() => {
    if (gameLoaded && iframeRef.current && containerRef.current) {
      const container = containerRef.current;
      const iframe = iframeRef.current;
      
      // Get actual viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Set container dimensions to exact viewport size
      container.style.width = `${viewportWidth}px`;
      container.style.height = `${viewportHeight}px`;
      
      // Set iframe dimensions with a small buffer to prevent content cutoff
      iframe.style.width = `${viewportWidth}px`;
      iframe.style.height = `${viewportHeight}px`;
      iframe.style.transform = 'scale(1)';
      iframe.style.transformOrigin = 'top left';
      
      // Force reflow and dispatch resize event
      setTimeout(() => {
        try {
          const resizeEvent = new Event('resize');
          iframe.contentWindow?.dispatchEvent(resizeEvent);
          
          // Also try postMessage for better cross-browser support
          iframe.contentWindow?.postMessage({ type: 'resize', width: viewportWidth, height: viewportHeight }, '*');
        } catch (error) {
          console.log('Could not dispatch resize event to iframe:', error);
        }
      }, 150);
    }
  }, [gameLoaded]);

  // Setup responsive viewport when component mounts
  useEffect(() => {
    // Set aggressive viewport for cross-browser compatibility
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover, shrink-to-fit=no');
    }
    
    // Lock body and html to prevent any scrolling/overflow issues
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100%';

    // Load game initially
    if (iframeRef.current && !iframeRef.current.src) {
      console.log('Loading responsive game:', game.gameUrl);
      iframeRef.current.src = game.gameUrl;
    }

    return () => {
      // Cleanup on unmount
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      }
      
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, [game.gameUrl]);

  // Handle orientation changes and resize game
  useEffect(() => {
    updateGameDimensions();
  }, [orientation, updateGameDimensions]);

  // Handle window resize for better cross-browser support
  useEffect(() => {
    const handleResize = () => {
      updateGameDimensions();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [updateGameDimensions]);

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-50 bg-black"
      style={{ 
        width: '100vw', 
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        margin: 0,
        padding: 0
      }}
    >
      {/* Back Button */}
      <div className="absolute top-4 left-4 z-50">
        <Button
          onClick={handleExit}
          variant="ghost"
          size="sm"
          className="bg-black/80 text-white hover:bg-black/90 backdrop-blur-sm border border-white/30"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>
      
      {/* Loading Indicator */}
      {!gameLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-30">
          <div className="flex flex-col items-center space-y-6">
            <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-white text-2xl font-medium">Loading game...</p>
          </div>
        </div>
      )}
      
      {/* Responsive Game Iframe */}
      <iframe
        ref={iframeRef}
        className="absolute inset-0 w-full h-full border-0 transition-all duration-300 ease-in-out"
        title={game.title}
        onLoad={handleGameLoad}
        onError={(e) => console.error('Responsive game iframe error:', e)}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        style={{ 
          display: 'block',
          background: 'black',
          width: '100vw',
          height: '100vh',
          border: 'none',
          outline: 'none',
          margin: 0,
          padding: 0
        }}
      />
    </div>
  );
};

export default LandscapeGamePlayer;
